import React, { useEffect, useRef, useState } from 'react';
import API_BASE_URL from '../config/api';
import {
  announceAcademicYearChange,
  getSelectedYear,
  setSelectedYear,
  startAcademicYearMonitor,
} from '../config/academicYear';
import './AcademicYearSelector.css';

/**
 * Ust barda akademik yil secici.
 *
 * Guncel yil disindaki yillar salt-okunurdur. Secicinin gorunumu durumdan
 * bagimsizdir; asil yazma engeli veritabaninda trigger ile zorlanir.
 */
const AcademicYearSelector = () => {
  const [years, setYears] = useState([]);
  const [selected, setSelected] = useState(getSelectedYear());
  const [open, setOpen] = useState(false);
  const selectorRef = useRef(null);

  useEffect(() => {
    return startAcademicYearMonitor(
      async () => {
        const response = await fetch(`${API_BASE_URL}/api/academic-years`);
        if (!response.ok) throw new Error('Akademik yillar yuklenemedi');
        return response.json();
      },
      (result) => {
        if (announceAcademicYearChange(result)) {
          window.location.reload();
          return;
        }
        setYears(result.years);
        setSelected(result.selected);
      }
    );
  }, []);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const activeMeta = years.find((y) => y.year === selected);
  const planning = activeMeta?.status === 'planning';
  const readOnly = activeMeta && !activeMeta.editable;
  const selectedLabel = activeMeta?.label || selected;

  return (
    <div
      className="academic-year-selector"
      ref={selectorRef}
      title={
        readOnly
          ? planning
            ? 'Hazırlık akademik yılı — ders planı EBS üzerinden yüklenir'
            : 'Geçmiş akademik yıl — yalnızca görüntülenebilir, değişiklik yapılamaz'
          : 'Aktif akademik yıl — bu yılın verileri düzenlenebilir'
      }
    >
      <button
        type="button"
        className="ay-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Akademik yıl seçimi"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ay-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M7 3v3M17 3v3M4.5 9h15M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
            <path d="M8 13h3M13 13h3M8 16h3M13 16h3" />
          </svg>
        </span>
        <span className="ay-value">{selectedLabel}</span>
        {activeMeta?.status === 'archived' && <span className="ay-archive-badge">Arşiv</span>}
        <span className={`ay-chevron${open ? ' is-open' : ''}`} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="ay-menu" role="listbox" aria-label="Akademik yıllar">
          {years.map((year) => (
            <button
              key={year.year}
              type="button"
              role="option"
              aria-selected={year.year === selected}
              className={`ay-option${year.year === selected ? ' is-selected' : ''}`}
              onClick={() => {
                setOpen(false);
                if (year.year !== selected) setSelectedYear(year.year);
              }}
            >
              {year.label}
              {year.status === 'archived' && <span className="ay-option-status">Yalnızca görüntüleme</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AcademicYearSelector;
